import React, { useRef } from "react";
import {
  Box,
  InlineStack,
  BlockStack,
  Text,
  Avatar,
  Button,
  Thumbnail,
} from "@shopify/polaris";
import axios from "../../Assets/Lib/axios";
import { useState } from "react";
import { DeleteIcon, EditIcon } from "@shopify/polaris-icons";
import parse from "html-react-parser";
import JoditEditor from "jodit-react";
import { useMemo } from "react";
import LoadingScreen from "../LoadingScreen";
import { ChevronRightIcon, ChevronDownIcon } from "@shopify/polaris-icons";

export default function CommentItem({
  comment,
  taskId,
  onDelete,
  onUpdate,
  onSent,
}) {
  const [isEditing, setIsEditing] = useState(false);
  const pastelColors = [
    "#FADADD",
    "#F9E79F",
    "#A3E4D7",
    "#AED6F1",
    "#D7BDE2",
    "#F5CBA7",
    "#ABEBC6",
    "#FAD7A0",
  ];

  const getColorForName = (name = "") => {
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    return pastelColors[Math.abs(hash) % pastelColors.length];
  };

  const getInitials = (name = "") =>
    name
      .split(" ")
      .filter(Boolean)
      .slice(0, 2)
      .map((n) => n[0].toUpperCase())
      .join("");
  const [text, setText] = useState("");
  const editorRef = useRef(null);
  const editorConfig = useMemo(
    () => ({
      readonly: false,
      toolbarAdaptive: false,
      toolbarSticky: false,
      toolbarButtonSize: "middle",
      buttons: ["bold", "italic", "underline", "|", "ul", "ol"],
      removeButtons: ["link", "source", "fullsize"],
      showXPathInStatusbar: false,
      showCharsCounter: false,
      showWordsCounter: false,
      showStatusbar: false,
      enableDragAndDropFileToEditor: false,
      spellcheck: false,
      askBeforePasteHTML: false,
      placeholder: "",
      extraButtons: [
        // {
        //   name: "customLink",
        //   iconURL:
        //     "https://img.icons8.com/material-outlined/24/000000/link.png",
        //   tooltip: "Insert Custom Link",
        //   exec: (editor) => {
        //     const url = prompt("Enter link URL");
        //     if (url) {
        //       const sel = editor.selection;
        //       if (sel.range) {
        //         editor.execCommand("createLink", false, url);
        //       }
        //     }
        //   },
        // },
        {
          name: "customImage",
          iconURL:
            "https://img.icons8.com/material-outlined/24/000000/image.png", // Custom icon URL
          exec: (editor) => {
            fileInputRef.current.click();
          },
          tooltip: "Insert Image",
        },
      ],
      uploader: {
        insertImageAsBase64URI: false, // Do not insert images as base64
      },
      // events: {
      //   paste: (e) => {
      //     handlePaste(e);
      //   },
      // },
    }),
    []
  );

  const [isReplying, setIsReplying] = useState(false);
  const [replyText, setReplyText] = useState("");
  const replyEditorRef = useRef(null);
  const replyEditorConfig = useMemo(
    () => ({
      readonly: false,
      toolbarAdaptive: false,
      toolbarSticky: false,
      toolbarButtonSize: "middle",
      buttons: ["bold", "italic", "underline", "|", "ul", "ol"],
      removeButtons: ["link", "source", "fullsize"],
      showXPathInStatusbar: false,
      showCharsCounter: false,
      showWordsCounter: false,
      showStatusbar: false,
      enableDragAndDropFileToEditor: false,
      spellcheck: false,
      askBeforePasteHTML: false,
      placeholder: "Write a reply...",
    }),
    []
  );
  const handleSendReply = async () => {
    if (!replyText) return;

    await axios.post(`/task-manager/${taskId}/comments/${comment.id}/replies`, {
      content: replyText,
      // parent_id: comment.id,
    });

    onSent();
    setReplyText("");
    setIsReplying(false);
  };
  const handlePaste = async (e) => {
    setIsImageUploading(true);
    const clipboard = e.clipboardData;
    if (!clipboard) return;

    const files = [];

    if (clipboard.items) {
      for (const item of clipboard.items) {
        if (item.type.startsWith("image/")) {
          const file = item.getAsFile();
          if (file) files.push(file);
        }
      }
    }

    if (!files.length) return;

    e.preventDefault();

    for (const file of files) {
      try {
        const preview = URL.createObjectURL(file);

        // ⬆️ show preview immediately
        setImages((prev) => [...prev, { preview, uploading: true }]);

        // ⬇️ upload to S3
        const key = await uploadToS3(file);

        // 🔁 replace temp image with uploaded one
        setImages((prev) =>
          prev.map((img) =>
            img.preview === preview ? { preview, key, uploading: false } : img
          )
        );
        setIsImageUploading(false);
      } catch (err) {
        console.error("Upload failed", err);
      }
    }
  };
  const [images, setImages] = useState([]);
  const [isImageUploading, setIsImageUploading] = useState(false);
  const [sending, setSending] = useState(false);
  const uploadToS3 = async (file) => {
    const formData = new FormData();
    formData.append("entity_type", "task_manager_comment_attachment");
    formData.append("image", file);

    const res = await axios.post("/s3/save-file", formData);

    return res.data.data.file_key; // 👈 same as your projects
  };
  const [showReplies, setShowReplies] = useState(false);
  const [editingReplyId, setEditingReplyId] = useState(null);
  const [replyEditText, setReplyEditText] = useState("");
  const replyEditConfig = useMemo(
    () => ({
      readonly: false,
      toolbarAdaptive: false,
      toolbarSticky: false,
      toolbarButtonSize: "middle",
      buttons: ["bold", "italic", "underline", "|", "ul", "ol"],
      removeButtons: ["link", "source", "fullsize"],
      showXPathInStatusbar: false,
      showCharsCounter: false,
      showWordsCounter: false,
      showStatusbar: false,
      enableDragAndDropFileToEditor: false,
      spellcheck: false,
      askBeforePasteHTML: false,
      placeholder: "",
    }),
    []
  );
  const handleSaveReply = async (replyId) => {
    await axios.patch(
      `/task-manager/${taskId}/comments/${comment.id}/replies/${replyId}`,
      { content: replyEditText }
    );
    onSent();
    setEditingReplyId(null);
    setReplyEditText("");
  };

  const handleDeleteReply = async (replyId) => {
    await axios.delete(
      `/task-manager/${taskId}/comments/${comment.id}/replies/${replyId}`
    );
    onSent();
  };
  const fileInputRef = useRef(null);

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      handleImageUpload(file);
    }
  };

  const handleImageUpload = async (file) => {
    console.log("aaaaaa");
    const formData = new FormData();
    formData.append("image", file);
    formData.append("entity_type", "task_manager_comment_attachment");

    try {
      const response = await axios.post("s3/save-file", formData);
      const imageUrl = response.data.data.file_url; // URL of the uploaded file
      console.log("imageUrl=", imageUrl);
      insertImageAtCursor(imageUrl);
      // const editorInstance = editor.current;
      setText((prev) => prev + `<img src="${imageUrl}" />`); // setInstructionValue(instructionValue + `<img src=${imageUrl}></img>`);
    } catch (error) {
      console.error("Error uploading image:", error);
    }
  };
  const insertImageAtCursor = (imageUrl) => {
    const editorInstance = editorRef.current?.editor;
    if (!editorInstance) return;

    editorInstance.selection.insertHTML(
      `<img src="${imageUrl}" alt="Image" />`
    );
    const updatedValue = editorInstance.value;
    setText(updatedValue);
  };

  function PersonAvatar({ person, getColorForName, getInitials }) {
    return (
      <div
        style={{
          width: "34px",
          height: "34px",
          borderRadius: "50%",
          overflow: "hidden",
          backgroundColor: person?.image_url
            ? "transparent"
            : getColorForName(person?.name),
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontWeight: 600,
          fontSize: "13px",
          color: "#333",
          flexShrink: 0,
        }}
      >
        {person?.image_url ? (
          <img
            src={person.image_url}
            alt=""
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        ) : (
          getInitials(person?.name)
        )}
      </div>
    );
  }

  return (
    <Box
      padding="300"
      background="bg-surface"
      borderRadius="200"
      style={{ width: "100%" }}
    >
      <InlineStack align="space-between">
        <InlineStack gap="150" align="start">
          {/* <div
            style={{
              width: "40px",
              height: "40px",
              borderRadius: "50%",
              overflow: "hidden",
              backgroundColor: comment.person?.image_url
                ? "transparent"
                : getColorForName(comment.person?.name),
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: 600,
              fontSize: "14px",
              color: "#333",
              flexShrink: 0,
            }}
          >
            {comment.person?.image_url ? (
              <img
                src={comment.person.image_url}
                alt=""
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                }}
              />
            ) : (
              getInitials(comment.person?.name)
            )}
          </div> */}
          <BlockStack gap="100" inlineAlignment="start">
            <InlineStack gap="150" align="start" blockAlign="center">
              <PersonAvatar
                person={comment.person}
                getColorForName={getColorForName}
                getInitials={getInitials}
              />
              <Text fontWeight="medium">{comment.person?.name ?? ""}</Text>
            </InlineStack>

            {isEditing ? (
              <>
                <input
                  type="file"
                  ref={fileInputRef}
                  style={{ display: "none" }}
                  onChange={handleFileChange}
                  accept="image/*"
                />
                <JoditEditor
                  value={text}
                  ref={editorRef}
                  onChange={(newValue) => setText(newValue)}
                  config={editorConfig}
                  // onPaste={handlePaste}
                />
                {/* <textarea
                  value={text}
                  placeholder="Write a comment…"
                  onChange={(e) => setText(e.target.value)}
                  style={{
                    width: "100%",
                    minHeight: "90px",
                    padding: "10px 12px",
                    borderRadius: "6px",
                    border: "1px solid #dfe3e8",
                    fontSize: "14px",
                    fontFamily: "inherit",
                    resize: "vertical",
                    outline: "none",
                  }}
                /> */}

                <InlineStack gap="200">
                  <Button
                    primary
                    onClick={async () => {
                      const res = await axios.patch(
                        `/task-manager/${taskId}/comments/${comment.id}`,
                        { content: text }
                      );
                      //onUpdate(res.data.data);
                      onSent();
                      setIsEditing(false);
                    }}
                  >
                    Save
                  </Button>

                  <Button onClick={() => setIsEditing(false)}>Cancel</Button>
                </InlineStack>
              </>
            ) : (
              <Text>{parse(comment.content)}</Text>
            )}
            {isReplying && (
              <Box
                paddingBlockStart="200"
                paddingInlineStart="400"
                width="100%"
              >
                <BlockStack gap="200">
                  <JoditEditor
                    ref={replyEditorRef}
                    value={replyText}
                    onChange={(v) => setReplyText(v)}
                    config={replyEditorConfig}
                  />

                  <InlineStack gap="200">
                    <Button
                      primary
                      onClick={handleSendReply}
                      disabled={!replyText}
                    >
                      Send
                    </Button>
                    <Button
                      onClick={() => {
                        setIsReplying(false);
                        setReplyText("");
                      }}
                    >
                      Cancel
                    </Button>
                  </InlineStack>
                </BlockStack>
              </Box>
            )}

            {/* Images */}
            {/* {comment.images_url?.length > 0 && (
              <InlineStack gap="100">
                {comment.images_url.map((img, i) => (
                  <div
                    key={i}
                    style={{ cursor: "pointer" }}
                    onClick={() => window.open(img, "_blank")}
                  >
                    <Thumbnail source={img} size="small" />
                  </div>
                ))}
              </InlineStack>
            )} */}

            {/* Actions */}
            {/* {comment.is_mine && ( */}
            <InlineStack gap="200">
              <Button
                variant="plain"
                onClick={() => {
                  setText(comment.content); // ✅ preload existing text
                  setIsEditing(true);
                }}
                disabled={!comment.is_mine}
              >
                Edit
              </Button>
              <Button
                variant="plain"
                onClick={() => {
                  setIsReplying(true);
                  setReplyText("");
                }}
              >
                Reply
              </Button>
              <Button
                variant="plain"
                destructive
                disabled={!comment.is_mine}
                onClick={async () => {
                  try {
                    await axios.delete(
                      `/task-manager/${taskId}/comments/${comment.id}`
                    );
                    onDelete(comment.id); // ✅ local state update ONLY
                  } catch (err) {
                    console.error("Delete failed", err);
                  }
                }}
              >
                Delete
              </Button>
              {comment.replies?.length > 0 && (
                <Button
                  variant="plain"
                  icon={showReplies ? ChevronDownIcon : ChevronRightIcon}
                  onClick={() => setShowReplies((v) => !v)}
                >
                  Replies ({comment.replies.length})
                </Button>
              )}
            </InlineStack>
            {showReplies && comment.replies?.length > 0 && (
              <Box
                paddingInlineStart="600"
                paddingBlockStart="200"
                width="100%"
              >
                <BlockStack gap="200">
                  {comment.replies.map((r) => (
                    <Box
                      key={r.id}
                      padding="200"
                      background="bg-surface-secondary"
                      borderRadius="200"
                      style={{ width: "100%" }}
                    >
                      <BlockStack gap="150">
                        <InlineStack
                          gap="150"
                          align="start"
                          blockAlign="center"
                        >
                          <PersonAvatar
                            person={r.person}
                            getColorForName={getColorForName}
                            getInitials={getInitials}
                          />
                          <Text fontWeight="medium">
                            {r.person?.name ?? ""}
                          </Text>
                        </InlineStack>

                        {editingReplyId === r.id ? (
                          <BlockStack gap="200">
                            <JoditEditor
                              value={replyEditText}
                              onChange={(v) => setReplyEditText(v)}
                              config={replyEditConfig}
                            />

                            <InlineStack gap="200">
                              <Button
                                primary
                                onClick={() => handleSaveReply(r.id)}
                              >
                                Save
                              </Button>
                              <Button
                                onClick={() => {
                                  setEditingReplyId(null);
                                  setReplyEditText("");
                                }}
                              >
                                Cancel
                              </Button>
                            </InlineStack>
                          </BlockStack>
                        ) : (
                          <Text>{parse(r.content)}</Text>
                        )}

                        {r.is_mine && (
                          <InlineStack gap="200">
                            <Button
                              variant="plain"
                              onClick={() => {
                                setEditingReplyId(r.id);
                                setReplyEditText(r.content);
                              }}
                            >
                              Edit
                            </Button>
                            <Button
                              variant="plain"
                              destructive
                              onClick={() => handleDeleteReply(r.id)}
                            >
                              Delete
                            </Button>
                          </InlineStack>
                        )}
                      </BlockStack>
                    </Box>
                  ))}
                </BlockStack>
              </Box>
            )}

            {/* )} */}
          </BlockStack>
        </InlineStack>
        {/* <InlineStack gap="200">
          <Button
            plain
            //  onClick={() => setIsEditing((v) => !v)}
            icon={EditIcon}
          ></Button>

          <Button
            plain
            destructive
            icon={DeleteIcon}
            // onClick={async () => {
            //   await axios.delete(`/task-manager/comments/${comment.id}`);
            //   onDelete(comment.id);
            // }}
          ></Button>
        </InlineStack> */}
      </InlineStack>
      {isImageUploading ? <LoadingScreen /> : null}
    </Box>
  );
}
